import { useState, useEffect } from 'react';
import {
  graphqlAccessQuery,
  graphqlTicketQuery,
  graphqlEventsByRestaurantsQuery,
} from '@/lib/api.ts';
import { useNavigate } from 'react-router-dom';
import { AuthAtom } from '../lib/state.ts';
import { useAtomValue } from 'jotai';
import styles from './AccessType.module.css';
import {
  useWidgetState,
  UserAtom,
  useRestaurantId,
  useRestaurant,
  RestaurantAtom,
} from '../lib/state.ts';
import { isPaymentReady } from '../lib/ready.ts';
import { LuChevronLeft } from 'react-icons/lu';
import dayjs from "dayjs";
import Config from "@/lib/Config.ts";

const range = (start, end) => {
  let arr = [];
  for (let i = start; i <= end; i++) {
    arr.push(i);
  }
  return arr;
};

export function AccessTypes() {
  const [screen, setScreen] = useState(0);
  const [eventId, setEventId] = useState();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const id = useRestaurantId();
  const user = useAtomValue(UserAtom);
  const auth = useAtomValue(AuthAtom);
  const { meta } = useRestaurant();
  const restaurant = useAtomValue(RestaurantAtom);
  const [accessTypes, setAccessTypes] = useState([]);
  const [ticketInventory, setTicketInventory] = useState([]);
  const [widget, updateWidget] = useWidgetState();
  const navigate = useNavigate();
  const user_ready = !!auth?.token && !!user?.phone;
  const payment_ready = user_ready && isPaymentReady(widget);
  const [selectedTicket, setSelectedTicket] = useState();

  // Hacky Zenyara-related variables until backend changes are made
  const ZENYARA_2025_ID: number = Config.data.environment === 'production' ? 758 : 731;
  const ZENYARA_HIDDEN_EVENT_IDS: number[] = Config.data.environment === 'production' ? [190, 191] : [45];

  useEffect(() => {
    if (!restaurant?.id) {
      return;
    }

    const fetchRestaurant = async () => {
      try {
        const eventInfo = await graphqlEventsByRestaurantsQuery(restaurant.id);

        // Filter to only upcoming (or same-day) events; hide events that have already passed
        const allEvents = eventInfo?.data?.getEventsByRestaurantId || [];
        const now = dayjs();
        const upcomingEvents = allEvents
          .filter((e) => {
            if (!e?.date) {
              return false;
            }
            // Keep if the event's day has not fully passed yet
            return !dayjs(e.date).endOf('day').isBefore(now);
          })
          .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

        const getEventId =
          upcomingEvents.length > 0 ? upcomingEvents[0].id : null;

        setEventId(getEventId);
        setEvents(upcomingEvents);
        setIsLoading(false);
      } catch (err) {
        console.log('Error fetching event info', err);
        setIsLoading(false);
      }
    };

    void fetchRestaurant();
  }, [restaurant?.id]);

  useEffect(() => {
    // Skip fetch if no eventId (all events are past or none exist)
    if (!eventId) {
      setIsLoading(false);
      return;
    }

    const fetchAccessTypes = async () => {
      try {
        const accessTypeQuery = await graphqlAccessQuery(eventId);

        if (accessTypeQuery && accessTypeQuery.data) {
          setAccessTypes(accessTypeQuery.data.accessTypes);
        }
      } catch (err) {
        console.log('Error fetching access types', err);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchAccessTypes();
  }, [eventId]);

  async function handleAccessType({ item }) {
    if (!item.id) {
      return;
    }

    try {
      const ticketQuery = await graphqlTicketQuery(eventId, item.id);

      setTicketInventory(ticketQuery.data.ticketInventory);
      setScreen(1);
    } catch (err) {
      console.log('Error fetching access types', err);
    }
  }

  function handleTicketOption({ ticketItem, count = 1 }) {
    if (!ticketItem) {
      return;
    }

    if (ticketItem.sold_out) {
      return;
    }

    updateWidget({
      confirm_body: {
        restaurant_id: id!,
        seating_id: ticketItem.id,
        party: ticketItem.guests,
      },
      date: '2024-07-04',
      availability: meta?.availability[0],
      option: {
        price__currency: ticketItem.price__currency,
        price: ticketItem.price,
        seats: ticketItem.guests,
        seating_id: ticketItem.id,
        type: null,
      },
      event: {
        id: eventId,
      },
      eventInfo: {
        id: eventId,
        ticektItemId: ticketItem.id,
        quantity: count,
      },
    });

    switch (true) {
      case payment_ready:
        navigate('../eventconfirm');
        break;
      case user_ready:
        navigate('../eventpayment');
        break;
      default:
        // Weird place for this, but make sure we don't have some old payment method set for a different user
        updateWidget({
          payment_method: undefined,
        });
        navigate('../eventinfo');
        break;
    }
  }

  if (selectedTicket && screen === 2) {
    return <div>
      <div onClick={() => setScreen(1)} className={styles.back}>
        <LuChevronLeft /> Back
      </div>
      <h3>Select Quantity</h3>
      {
        range(1, selectedTicket.maximum_purchasable).map((quantity: number) => {
          return <div className={styles.eventName} onClick={() => {
            handleTicketOption({ ticketItem: selectedTicket, count: quantity });
          }}>
            {quantity}
          </div>
        })
      }

    </div>
  }

  if (ticketInventory.length > 0 && screen === 1) {
    return (
        <div>
          <div className={styles.chooseSection}>
            <div onClick={() => setScreen(0)} className={styles.back}>
              <LuChevronLeft /> Back
            </div>

            {ticketInventory.map((ticket, index) => {

              return (
                  <div
                      key={ticket.id}
                      className={styles.accessItem}
                      onClick={() => {
                        setScreen(2);
                        setSelectedTicket(ticket);

                        if (ticket.maximum_purchasable === 1) {
                          handleTicketOption({ ticketItem: ticket, count: 1 })
                        }
                      }}
                  >
                    <div className={styles.info}>{ticket.item.name}</div>
                    {ticket.item.description && (
                      <div className={styles.description}>{ticket.item.description}</div>
                    )}
                    <div>{ticket.guests} guest{ticket.guests !== 1 ? 's' : ''}</div>
                    <div>{ticket.price__currency}</div>
                    <div>{ticket.sold_out ? 'Sold Out' : ''}</div>
                  </div>
              );
            })}
          </div>
        </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.chooseSection}>
        <h2>Loading...</h2>
      </div>
    );
  }

  return (
      <div style={{ width: '100%' }}>
        <h3>Select Date</h3>

        <div style={{ display: 'flex', flex: 'auto', flexDirection: 'row', width: '100%', paddingBottom: '20px', justifyContent: 'flex-start', alignItems: 'center', overflow: 'auto', minWidth: '100%', maxWidth: '100%', borderBottom: '1px solid #ddd' }}>
          {events.map((event) => {

            if (Number(id) === ZENYARA_2025_ID && ZENYARA_HIDDEN_EVENT_IDS.includes(Number(event.id)) ) {
              return null;
            }

            return <button key={event.id} onClick={() => setEventId(event.id)} className={ eventId === event.id ? styles.eventNameSelected : styles.eventName }>
              <span style={{ fontSize: '1rem' }}>{ dayjs(event.date).format('ddd, MMM D') }</span>
            </button>
          })}
        </div>

        <div>
          {  accessTypes.length === 0 && <div className={styles.chooseSection}>
            <h2>Sorry, there are no tickets available for this event.</h2>
          </div>}
        </div>

        {
          accessTypes.length > 0 &&
            <h3>Select Type</h3>
        }

        <div className={styles.chooseSection}>
          {accessTypes.map((acc) => {

            // This is a super hacky fix while the backend changes are made
            if (Number(id) === ZENYARA_2025_ID && ! acc.name.endsWith('Pass') && ! acc.name.endsWith('Passes')) {
              return null;
            }

            return (
                <div
                    key={acc.id}
                    className={styles.accessItem}
                    onClick={() => {
                      handleAccessType({ item: acc });
                    }}
                >
                  <h2>{acc.name}</h2>
                  <div className={styles.info}>{acc.guest_summary}</div>
                  <div className={styles.description}>{acc.description}</div>
                  <div>{acc.sold_out ? 'sold out' : ''}</div>
                </div>
            );
          })}
        </div>
      </div>
  );
}
